import React, { useState, useEffect } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { deleteMyAccount, saveEmailOptOut } from "../services/userService";
import { useAuth } from "../context/AuthContext";
import type RootStackParamList from "../navigation/RootStackParamList";
import { TOPICS, type TopicId } from "../constants/topics";
import PasswordInput from "./Auth/ui/PasswordInput";
import Constants from "expo-constants";
import * as Updates from "expo-updates";
import { buildVersionInfo } from "../utils/versionInfo";

// Topics a user can follow. "anything" is excluded — following "anything" would
// mean "notify me about everything", which defeats interest matching.
const INTEREST_TOPICS = TOPICS.filter((topic) => topic.id !== "anything");

export default function ProfileScreen() {
  const { user } = useAuth();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  // Profile Details State
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileSuccessMsg, setProfileSuccessMsg] = useState<string | null>(
    null,
  );
  const [profileErrorMsg, setProfileErrorMsg] = useState<string | null>(null);

  // Change Password State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordSuccessMsg, setPasswordSuccessMsg] = useState<string | null>(
    null,
  );
  const [passwordErrorMsg, setPasswordErrorMsg] = useState<string | null>(null);

  const [interests, setInterests] = useState<TopicId[]>([]);
  const [isUpdatingInterests, setIsUpdatingInterests] = useState(false);
  const [interestsSuccessMsg, setInterestsSuccessMsg] = useState<string | null>(
    null,
  );
  const [interestsErrorMsg, setInterestsErrorMsg] = useState<string | null>(
    null,
  );

  // Email preferences (Meldpot #103). emailSubscribed mirrors the inverse of
  // the user doc's emailOptOut flag: true means "receive tips & updates".
  const [emailSubscribed, setEmailSubscribed] = useState(true);
  const [isSavingEmailPref, setIsSavingEmailPref] = useState(false);
  const [emailPrefErrorMsg, setEmailPrefErrorMsg] = useState<string | null>(
    null,
  );

  // Account deletion (Meldpot #101). Two-step inline confirm rather than a
  // native dialog, which is unreliable on web.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setEmail(user.email ?? "Anonymous User");

      // Load current name from Firestore first, fallback to Auth profile
      const loadUserData = async () => {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(userDocRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDisplayName(data.name ?? user.displayName ?? "");
            setInterests(
              Array.isArray(data.interests)
                ? (data.interests as TopicId[])
                : [],
            );
            setEmailSubscribed(data.emailOptOut !== true);
          } else {
            setDisplayName(user.displayName ?? "");
          }
        } catch (err) {
          console.error("Error loading user data from Firestore:", err);
          setDisplayName(user.displayName ?? "");
        }
      };

      loadUserData();
    }
  }, [user]);

  const toggleInterest = (topicId: TopicId) => {
    setInterestsSuccessMsg(null);
    setInterestsErrorMsg(null);
    setInterests((prev) =>
      prev.includes(topicId)
        ? prev.filter((id) => id !== topicId)
        : [...prev, topicId],
    );
  };

  const handleSaveInterests = async () => {
    if (!user) return;
    setInterestsSuccessMsg(null);
    setInterestsErrorMsg(null);
    setIsUpdatingInterests(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { interests });
      setInterestsSuccessMsg("Interests saved.");
    } catch (error: any) {
      console.error("Error saving interests:", error);
      setInterestsErrorMsg(error?.message ?? "Failed to save interests.");
    } finally {
      setIsUpdatingInterests(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;

    setProfileSuccessMsg(null);
    setProfileErrorMsg(null);
    const trimmedName = displayName.trim();

    if (!trimmedName) {
      setProfileErrorMsg("Display name cannot be empty.");
      return;
    }

    setIsUpdatingProfile(true);
    try {
      // 1. Update Firebase Auth profile (live User instance — see
      // handleChangePassword)
      await updateProfile(auth.currentUser ?? user, {
        displayName: trimmedName,
      });

      // 2. Update Firestore user document
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { name: trimmedName });

      // Reload auth state on the live User instance. The context `user` can be
      // a non-live reference whose prototype methods (reload, …) are missing on
      // native/Hermes, which throws "undefined is not a function" (#131) — so
      // reload via auth.currentUser, matching updateProfile above.
      await (auth.currentUser ?? user).reload();
      setProfileSuccessMsg("Profile details updated successfully!");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      setProfileErrorMsg(error?.message ?? "Failed to update profile.");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;

    setPasswordSuccessMsg(null);
    setPasswordErrorMsg(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordErrorMsg("All password fields are required.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordErrorMsg("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordErrorMsg("New passwords do not match.");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      // Reauthenticate/updatePassword need the live Firebase User instance. A
      // copied user object (stale tab, spread state) lacks its prototype and
      // fails with "_updateTokensIfNecessary is not a function" (#125, #131),
      // so always use auth.currentUser here.
      const authUser = auth.currentUser;
      if (!authUser) {
        throw new Error("You are signed out. Please sign in and try again.");
      }

      // 1. Reauthenticate user first (required by Firebase for sensitive actions)
      if (authUser.email) {
        const credential = EmailAuthProvider.credential(
          authUser.email,
          currentPassword,
        );
        await reauthenticateWithCredential(authUser, credential);
      } else {
        throw new Error("No email associated with this account.");
      }

      // 2. Update the password
      await updatePassword(authUser, newPassword);

      // 3. Reset form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccessMsg("Password changed successfully!");
    } catch (error: any) {
      console.error("Error changing password:", error);
      // Firebase JS SDK v9+ returns "auth/invalid-credential" for a wrong
      // reauthentication password; older builds used "auth/wrong-password".
      if (
        error.code === "auth/invalid-credential" ||
        error.code === "auth/wrong-password"
      ) {
        setPasswordErrorMsg("The current password you entered is incorrect.");
      } else if (error.code === "auth/weak-password") {
        setPasswordErrorMsg(
          "The new password is too weak. Use at least 6 characters.",
        );
      } else if (error.code === "auth/requires-recent-login") {
        setPasswordErrorMsg(
          "For security, please sign out and sign back in, then try again.",
        );
      } else if (error.code === "auth/too-many-requests") {
        setPasswordErrorMsg("Too many attempts. Please try again later.");
      } else if (error.code === "auth/network-request-failed") {
        setPasswordErrorMsg(
          "Network error. Please check your connection and try again.",
        );
      } else {
        setPasswordErrorMsg("Failed to change password. Please try again.");
      }
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleToggleEmailSubscribed = async (nextSubscribed: boolean) => {
    if (!user || isSavingEmailPref) return;
    const previous = emailSubscribed;
    setEmailSubscribed(nextSubscribed);
    setEmailPrefErrorMsg(null);
    setIsSavingEmailPref(true);
    try {
      await saveEmailOptOut(user.uid, !nextSubscribed);
    } catch (error: any) {
      console.error("Error saving email preference:", error);
      setEmailSubscribed(previous);
      setEmailPrefErrorMsg(
        error?.message ?? "Failed to save your email preference.",
      );
    } finally {
      setIsSavingEmailPref(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeleting) return;
    setDeleteErrorMsg(null);
    setIsDeleting(true);
    try {
      // On success the auth listener swaps the app back to the guest/auth
      // flow, so this screen unmounts — no navigation needed here.
      await deleteMyAccount();
    } catch (error: any) {
      console.error("Error deleting account:", error);
      setDeleteErrorMsg(
        error?.message ?? "Failed to delete your account. Please try again.",
      );
      setIsDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const isAnonymous = user?.isAnonymous === true;

  return (
    <SafeAreaView style={styles.screen}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.container}>
            {/* Header / Back navigation */}
            <View style={styles.header}>
              <Pressable
                onPress={() => navigation.goBack()}
                style={styles.backButton}
                accessibilityRole="button"
                accessibilityLabel="Go back"
              >
                <Ionicons name="arrow-back" size={20} color="#3A6FB1" />
                <Text style={styles.backButtonText}>Back</Text>
              </Pressable>
              <Text style={styles.title}>My Profile</Text>
            </View>

            {/* Profile Card */}
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>User Details</Text>

              {/* Email Address (Read-only) */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Email Address</Text>
                <TextInput
                  value={email}
                  editable={false}
                  style={[styles.input, styles.inputDisabled]}
                />
                <Text style={styles.helperText}>
                  Your email address cannot be changed.
                </Text>
              </View>

              {/* Display Name */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Full Name</Text>
                <TextInput
                  value={displayName}
                  onChangeText={(text) => {
                    setDisplayName(text);
                    setProfileErrorMsg(null);
                    setProfileSuccessMsg(null);
                  }}
                  placeholder="Enter your full name"
                  placeholderTextColor="rgba(43,43,47,0.4)"
                  style={styles.input}
                />
              </View>

              {profileSuccessMsg && (
                <Text style={styles.successText}>{profileSuccessMsg}</Text>
              )}
              {profileErrorMsg && (
                <Text style={styles.errorText}>{profileErrorMsg}</Text>
              )}

              <Pressable
                onPress={handleUpdateProfile}
                disabled={isUpdatingProfile}
                style={[
                  styles.submitButton,
                  isUpdatingProfile && styles.buttonDisabled,
                ]}
              >
                {isUpdatingProfile ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Save Details</Text>
                )}
              </Pressable>
            </View>

            {/* Interests Card */}
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Interests</Text>
              <Text style={styles.helperText}>
                Pick the topics you care about and we&apos;ll email you when a
                matching conversation opens up.
              </Text>

              <View style={styles.chipRow}>
                {INTEREST_TOPICS.map((topic) => {
                  const selected = interests.includes(topic.id);
                  return (
                    <Pressable
                      key={topic.id}
                      onPress={() => toggleInterest(topic.id)}
                      style={[styles.chip, selected && styles.chipSelected]}
                    >
                      <Text
                        style={[
                          styles.chipText,
                          selected && styles.chipTextSelected,
                        ]}
                      >
                        {topic.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              {interestsSuccessMsg && (
                <Text style={styles.successText}>{interestsSuccessMsg}</Text>
              )}
              {interestsErrorMsg && (
                <Text style={styles.errorText}>{interestsErrorMsg}</Text>
              )}

              <Pressable
                onPress={handleSaveInterests}
                disabled={isUpdatingInterests}
                style={[
                  styles.submitButton,
                  isUpdatingInterests && styles.buttonDisabled,
                ]}
              >
                {isUpdatingInterests ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Save Interests</Text>
                )}
              </Pressable>
            </View>

            {/* Change Password Card */}
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Security & Password</Text>

              {isAnonymous ? (
                <View style={styles.anonymousBanner}>
                  <Ionicons
                    name="information-circle-outline"
                    size={24}
                    color="#3A6FB1"
                  />
                  <Text style={styles.anonymousText}>
                    You are currently signed in with a guest account. Register
                    to configure a password for security.
                  </Text>
                </View>
              ) : (
                <>
                  {/* Current Password */}
                  <View style={styles.inputGroup}>
                    <Text style={styles.label}>Current Password</Text>
                    <PasswordInput
                      value={currentPassword}
                      onChangeText={(text) => {
                        setCurrentPassword(text);
                        setPasswordErrorMsg(null);
                        setPasswordSuccessMsg(null);
                      }}
                      placeholder="Enter current password"
                      placeholderTextColor="rgba(43,43,47,0.4)"
                      style={styles.input}
                    />
                  </View>

                  {/* New Password */}
                  <View style={styles.inputGroup}>
                    <Text style={styles.label}>New Password</Text>
                    <PasswordInput
                      value={newPassword}
                      onChangeText={(text) => {
                        setNewPassword(text);
                        setPasswordErrorMsg(null);
                        setPasswordSuccessMsg(null);
                      }}
                      placeholder="Enter new password (min. 6 chars)"
                      placeholderTextColor="rgba(43,43,47,0.4)"
                      style={styles.input}
                    />
                  </View>

                  {/* Confirm Password */}
                  <View style={styles.inputGroup}>
                    <Text style={styles.label}>Confirm New Password</Text>
                    <PasswordInput
                      value={confirmPassword}
                      onChangeText={(text) => {
                        setConfirmPassword(text);
                        setPasswordErrorMsg(null);
                        setPasswordSuccessMsg(null);
                      }}
                      placeholder="Confirm new password"
                      placeholderTextColor="rgba(43,43,47,0.4)"
                      style={styles.input}
                    />
                  </View>

                  {passwordSuccessMsg && (
                    <Text style={styles.successText}>{passwordSuccessMsg}</Text>
                  )}
                  {passwordErrorMsg && (
                    <Text style={styles.errorText}>{passwordErrorMsg}</Text>
                  )}

                  <Pressable
                    onPress={handleChangePassword}
                    disabled={isUpdatingPassword}
                    style={[
                      styles.submitButton,
                      isUpdatingPassword && styles.buttonDisabled,
                    ]}
                  >
                    {isUpdatingPassword ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.submitButtonText}>
                        Change Password
                      </Text>
                    )}
                  </Pressable>
                </>
              )}
            </View>

            {/* Email Preferences Card */}
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Email Preferences</Text>
              <View style={styles.prefRow}>
                <View style={styles.prefTextWrap}>
                  <Text style={styles.prefTitle}>Tips & updates</Text>
                  <Text style={styles.helperText}>
                    Occasional emails with tips and conversation nudges. You
                    will still get essential emails about conversations you
                    book.
                  </Text>
                </View>
                <Switch
                  value={emailSubscribed}
                  onValueChange={handleToggleEmailSubscribed}
                  disabled={isSavingEmailPref}
                  trackColor={{ false: "#CBD2DA", true: "#3A6FB1" }}
                />
              </View>
              {emailPrefErrorMsg && (
                <Text style={styles.errorText}>{emailPrefErrorMsg}</Text>
              )}
            </View>

            {/* Danger Zone Card */}
            <View style={[styles.card, styles.dangerCard]}>
              <Text style={[styles.sectionTitle, styles.dangerTitle]}>
                Delete Account
              </Text>
              <Text style={styles.helperText}>
                Permanently delete your account and all associated data. This
                cannot be undone.
              </Text>

              {deleteErrorMsg && (
                <Text style={styles.errorText}>{deleteErrorMsg}</Text>
              )}

              {confirmingDelete ? (
                <View style={styles.confirmRow}>
                  <Pressable
                    onPress={() => setConfirmingDelete(false)}
                    disabled={isDeleting}
                    style={[styles.cancelButton, styles.confirmButton]}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleDeleteAccount}
                    disabled={isDeleting}
                    style={[
                      styles.deleteButton,
                      styles.confirmButton,
                      isDeleting && styles.buttonDisabled,
                    ]}
                  >
                    {isDeleting ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.deleteButtonText}>
                        Yes, delete forever
                      </Text>
                    )}
                  </Pressable>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    setDeleteErrorMsg(null);
                    setConfirmingDelete(true);
                  }}
                  style={styles.deleteButton}
                >
                  <Text style={styles.deleteButtonText}>Delete Account</Text>
                </Pressable>
              )}
            </View>

            <View style={styles.card}>
              <Text style={styles.sectionTitle}>App info</Text>
              {buildVersionInfo({
                appVersion: Constants.expoConfig?.version,
                runtimeVersion: Updates.runtimeVersion,
                channel: Updates.channel,
                updateId: Updates.updateId,
                createdAt: Updates.createdAt,
                isEmbeddedLaunch: Updates.isEmbeddedLaunch,
                platform: Platform.OS,
              }).map((row) => (
                <View key={row.label} style={styles.versionRow}>
                  <Text style={styles.versionLabel}>{row.label}</Text>
                  <Text style={styles.versionValue} selectable>
                    {row.value}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#F6F7F9",
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 40,
  },
  container: {
    width: "100%",
    maxWidth: 600,
    alignSelf: "center",
    paddingHorizontal: 24,
    gap: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 24,
    paddingBottom: 8,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.15)",
  },
  backButtonText: {
    fontFamily: "Lexend",
    fontSize: 14,
    fontWeight: "700",
    color: "#3A6FB1",
  },
  title: {
    fontFamily: "Lexend",
    fontSize: 26,
    fontWeight: "700",
    color: "#2B2B2F",
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.08)",
    padding: 24,
    gap: 16,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 999,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.25)",
  },
  chipSelected: {
    backgroundColor: "#3A6FB1",
    borderColor: "#3A6FB1",
  },
  chipText: {
    fontFamily: "Lexend",
    fontSize: 13,
    color: "rgba(43, 43, 47, 0.75)",
  },
  chipTextSelected: {
    color: "#FFFFFF",
  },
  sectionTitle: {
    fontFamily: "Lexend",
    fontSize: 18,
    fontWeight: "700",
    color: "#2B2B2F",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(35, 35, 53, 0.06)",
    paddingBottom: 10,
    marginBottom: 4,
  },
  inputGroup: {
    gap: 6,
  },
  label: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "700",
    color: "#2B2B2F",
    textTransform: "uppercase",
  },
  input: {
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.15)",
    paddingHorizontal: 12,
    backgroundColor: "#FFFFFF",
    fontSize: 15,
    fontFamily: "Lexend",
    color: "#2B2B2F",
  },
  inputDisabled: {
    backgroundColor: "#EEF0F3",
    borderColor: "rgba(35, 35, 53, 0.08)",
    color: "rgba(43, 43, 47, 0.5)",
  },
  helperText: {
    fontSize: 11,
    fontFamily: "Lexend",
    color: "rgba(43, 43, 47, 0.45)",
    marginTop: 2,
  },
  successText: {
    color: "#2E7D32",
    fontSize: 13,
    fontFamily: "Lexend",
    fontWeight: "500",
    textAlign: "center",
  },
  errorText: {
    color: "#C62828",
    fontSize: 13,
    fontFamily: "Lexend",
    fontWeight: "500",
    textAlign: "center",
  },
  submitButton: {
    height: 46,
    borderRadius: 12,
    backgroundColor: "#3A6FB1",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 6,
  },
  submitButtonText: {
    fontFamily: "Lexend",
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textTransform: "uppercase",
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  anonymousBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: "rgba(58, 111, 177, 0.08)",
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.15)",
  },
  anonymousText: {
    flex: 1,
    fontFamily: "Lexend",
    fontSize: 13,
    color: "#3A6FB1",
    lineHeight: 18,
  },
  prefRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  prefTextWrap: {
    flex: 1,
    gap: 2,
  },
  prefTitle: {
    fontFamily: "Lexend",
    fontSize: 15,
    fontWeight: "600",
    color: "#2B2B2F",
  },
  dangerCard: {
    borderColor: "rgba(198, 40, 40, 0.25)",
  },
  dangerTitle: {
    color: "#C62828",
    borderBottomColor: "rgba(198, 40, 40, 0.12)",
  },
  deleteButton: {
    height: 46,
    borderRadius: 12,
    backgroundColor: "#C62828",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 6,
  },
  deleteButtonText: {
    fontFamily: "Lexend",
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textTransform: "uppercase",
  },
  cancelButton: {
    height: 46,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.15)",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 6,
  },
  cancelButtonText: {
    fontFamily: "Lexend",
    fontSize: 14,
    fontWeight: "700",
    color: "#2B2B2F",
    textTransform: "uppercase",
  },
  confirmRow: {
    flexDirection: "row",
    gap: 12,
  },
  confirmButton: {
    flex: 1,
  },
  versionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: 6,
    gap: 12,
  },
  versionLabel: {
    fontSize: 13,
    color: "#6B7280",
  },
  versionValue: {
    fontSize: 13,
    fontWeight: "600",
    color: "#2B2B2F",
    flexShrink: 1,
    textAlign: "right",
  },
});
